import React from 'react';
import LegalPageHeader from './components/LegalPageHeader';
import LegalPageFooter from './components/LegalPageFooter';
import TermsSection from './components/TermsSection';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <LegalPageHeader
        title="SiteProof Ltd Terms and Conditions"
        lastUpdated="1 March 2026"
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Company identification */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8">
          <p className="text-sm text-slate-400">
            These Terms and Conditions govern your use of the SiteProof platform, operated by{' '}
            <strong className="text-slate-200">SiteProof Ltd</strong>, a company registered in England and Wales
            (Company number: <strong className="text-slate-200">17085500</strong>). By accessing or using SiteProof,
            you agree to be bound by these terms.
          </p>
        </div>

        <TermsSection number="1" title="Service Description">
          <p>
            SiteProof is a construction site management platform that enables companies to manage compliance,
            tasks, documents, snags, incidents, and team communications across construction sites.
          </p>
          <p>
            SiteProof Ltd grants you a limited, non-exclusive, non-transferable licence to access and use the
            SiteProof platform in accordance with these Terms and your subscription plan.
          </p>
          <p>
            The platform is provided on a software-as-a-service (SaaS) basis. Features available to you depend
            on your chosen subscription tier.
          </p>
        </TermsSection>

        <TermsSection number="2" title="User Responsibilities">
          <p>By using SiteProof, you agree to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the confidentiality of your account credentials</li>
            <li>Use the platform only for lawful purposes and in accordance with applicable law</li>
            <li>Not attempt to gain unauthorised access to any part of the platform</li>
            <li>Not upload content that is unlawful, harmful, or infringes third-party rights</li>
            <li>Ensure all workers and users added to your account have consented to their data being processed</li>
          </ul>
          <p>
            You are responsible for all activity that occurs under your account. You must notify SiteProof Ltd
            immediately if you suspect any unauthorised use of your account.
          </p>
        </TermsSection>

        <TermsSection number="3" title="Data Protection">
          <p>
            SiteProof Ltd processes personal data in accordance with the UK General Data Protection Regulation
            (UK GDPR) and the Data Protection Act 2018. SiteProof Ltd acts as a data processor in respect of
            personal data you upload to the platform, and you act as the data controller.
          </p>
          <p>
            By using SiteProof, you warrant that you have a lawful basis for processing the personal data of
            workers, clients, and other individuals whose data you input into the platform.
          </p>
          <p>
            For full details of how SiteProof Ltd handles personal data, please refer to our{' '}
            <a href="/privacy-policy" className="text-emerald-400 hover:text-emerald-300 underline">
              Privacy Policy
            </a>.
          </p>
        </TermsSection>

        <TermsSection number="4" title="Subscription and Payment">
          <p>
            Access to SiteProof requires a valid subscription. Subscription fees are charged in advance on a
            monthly or annual basis, depending on your chosen plan.
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>All fees are exclusive of VAT unless otherwise stated</li>
            <li>Payment is processed securely via Stripe</li>
            <li>Subscriptions renew automatically unless cancelled before the renewal date</li>
            <li>Refunds are not provided for partial subscription periods</li>
          </ul>
          <p>
            SiteProof Ltd reserves the right to modify pricing with 30 days' notice. Continued use of the
            platform after a price change constitutes acceptance of the new pricing.
          </p>
        </TermsSection>

        <TermsSection number="5" title="Intellectual Property">
          <p>
            All intellectual property rights in the SiteProof platform, including software, design, trademarks,
            and documentation, are owned by SiteProof Ltd or its licensors.
          </p>
          <p>
            You retain ownership of all data and content you upload to SiteProof. By uploading content, you
            grant SiteProof Ltd a limited licence to process and store that content solely for the purpose of
            providing the service to you.
          </p>
        </TermsSection>

        <TermsSection number="6" title="Liability Limitations">
          <p>
            To the maximum extent permitted by applicable law, SiteProof Ltd shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, including loss of profits, data,
            or business opportunities.
          </p>
          <p>
            SiteProof Ltd's total aggregate liability to you in connection with these Terms shall not exceed the
            total fees paid by you to SiteProof Ltd in the 12 months preceding the event giving rise to the claim.
          </p>
          <p>
            Nothing in these Terms limits or excludes liability for death or personal injury caused by
            negligence, fraud, or any other liability that cannot be excluded by law.
          </p>
        </TermsSection>

        <TermsSection number="7" title="Availability and Maintenance">
          <p>
            SiteProof Ltd aims to provide a reliable and available service but does not guarantee uninterrupted
            access. Scheduled maintenance will be communicated in advance where possible.
          </p>
          <p>
            SiteProof Ltd reserves the right to modify, suspend, or discontinue any part of the platform with
            reasonable notice.
          </p>
        </TermsSection>

        <TermsSection number="8" title="Termination">
          <p>
            Either party may terminate the subscription at any time. Upon termination:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Your access to the platform will cease at the end of the current billing period</li>
            <li>You may export your data prior to termination</li>
            <li>SiteProof Ltd will retain your data for 30 days after termination, after which it may be deleted</li>
          </ul>
          <p>
            SiteProof Ltd may terminate or suspend your account immediately if you breach these Terms or engage
            in conduct that SiteProof Ltd reasonably considers harmful to the platform or other users.
          </p>
        </TermsSection>

        <TermsSection number="9" title="Governing Law">
          <p>
            These Terms and Conditions are governed by and construed in accordance with the laws of England and
            Wales. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the
            courts of England and Wales.
          </p>
        </TermsSection>

        <TermsSection number="10" title="Contact">
          <p>
            If you have any questions about these Terms and Conditions, please contact SiteProof Ltd at:
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-3">
            <p className="font-medium text-slate-200">SiteProof Ltd</p>
            <p>Registered in England and Wales</p>
            <p>Company number: 17085500</p>
            <p>
              Email:{' '}
              <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
                support@site-proof.app
              </a>
            </p>
          </div>
        </TermsSection>
      </main>

      <LegalPageFooter crossLinkLabel="Privacy Policy" crossLinkTo="/privacy-policy" />
    </div>
  );
};

export default TermsAndConditions;
